import { useCallback, useState } from 'react';
import { Button, TextInput, Modal } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';

import { SURVEY_NAME_MAX_LENGTH } from '../../../domain/Surveys/Survey/Survey';
import { FEEDBACK_NAME_MAX_LENGTH } from '../../../domain/IncomingFeedback/Feedback';

const NAME_MAX_LENGTH = {
  survey: SURVEY_NAME_MAX_LENGTH,
  widget: FEEDBACK_NAME_MAX_LENGTH,
};

export interface EntityRenameModalProps {
  entityType: 'survey' | 'widget';
  entityName: string | undefined;
  isOpen: boolean;
  isRenamePending: boolean;
  onClose: () => void;
  onRename: (name: string) => void;
}

export function EntityRenameModal({
  isOpen,
  isRenamePending,
  onClose,
  onRename,
  entityName,
  entityType,
}: EntityRenameModalProps): JSX.Element | null {
  const { t } = useTranslation('feedback');
  const [name, setName] = useState<string>(entityName ?? '');

  const handleRenameClick = useCallback(() => {
    if (!name) {
      return;
    }
    onRename(name);
  }, [onRename, name]);

  if (!isOpen) return null;

  return (
    <Modal
      contentLabel={t('EntityActions.Move.entity', { entityType })}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Modal.Header title={t('EntityActions.Rename.modal-title', { entityType })} />
      <Modal.Body>
        <TextInput
          aria-label={t('EntityActions.Rename.text-aria-label')}
          size="l"
          onChange={(e) => setName(e.target.value)}
          value={name}
          maxLength={NAME_MAX_LENGTH[entityType]}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" isLoading={isRenamePending} onClick={onClose}>
          {t('EntityActions.Rename.cancel')}
        </Button>
        <Button
          kind="primary"
          isDisabled={name.length === 0}
          isLoading={isRenamePending}
          onClick={handleRenameClick}
        >
          {t('EntityActions.Rename.save-changes', { entityType })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
